'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'
import { Formik } from 'formik'

// walrus
import Box, { BoxProps } from '@components/kit/box'
import Button from '@components/kit/button'
import FlatList from '@components/kit/flat-list'
import Loading from '@components/kit/loading'
import Modal from '@components/kit/modal'
import Pressable from '@components/kit/pressable'
import ScrollView from '@components/kit/scroll-view'
import Text from '@components/kit/text'
import Form from '@components/kit/form'

// lib
import { ProfileContext } from '../profile'
import MemberInfoPopper from './member-info-popper'
import { ChatContext } from '../../chat.web'
import { useAllergyStore } from '@store/Allergy'
import { useMedsStore } from '@store/Meds'
import { IAllergy, IAllergyInsert } from '@store/Allergy/types'
import { ISearchedMed } from '@store/Meds/types'
import { TextFieldFormated, SelectFieldFormated } from '@components/formFields'
import { HorizontalRule } from '@components/table'

/* -----------------------------------------------------------------------------
 * Local components
 * -------------------------------------------------------------------------- */

const FormHorizontalRule = ({
  marginY = '08',
  height = '2px',
  width = '100%',
  style = {},
  ...props
}: BoxProps) => (
  <Box
    width={width}
    height={height}
    marginY={marginY as any}
    style={{ backgroundColor: '#F9F9F9', ...(style as any) }}
    {...props}
  />
)

const Row = ({ children }) => (
  <Box flexDirection='row' justifyContent='space-between' alignItems='center'>
    {children}
  </Box>
)

const LeftColumn = ({ children }) => (
  <Box justifyContent='flex-start' alignItems='center'>
    <Text font='display.14.bold'>{children}</Text>
  </Box>
)

const RightColumn = ({ children }) => (
  <Box justifyContent='flex-end' alignItems='center'>
    {children}
  </Box>
)

/* -----------------------------------------------------------------------------
 * MenuItem
 * -------------------------------------------------------------------------- */

type MenuItemProps = {
  onPress?: () => void
  label: string
  lastItem?: boolean
}

const MenuItem = ({ onPress, label, lastItem }: MenuItemProps) => (
  <React.Fragment>
    <Pressable onPress={onPress} paddingX='04' paddingY='08'>
      <Text textColor='newPurple' font='display.14.bold'>
        {label}
      </Text>
    </Pressable>
    {!lastItem && <HorizontalRule height='1px' />}
  </React.Fragment>
)

/* -----------------------------------------------------------------------------
 * SearchAllergy (Step 1)
 * -------------------------------------------------------------------------- */

interface SearchAllergyProps {
  onSelect: (medication: ISearchedMed) => void
}

function SearchAllergy({ onSelect }: SearchAllergyProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [lastTimeout, setLastTimeout] = useState<number | null>(null)
  const [medName, setMedName] = useState('')
  const [medications, setMedications] = useState<ISearchedMed[]>([])
  const [preventTyping, setPreventTyping] = useState(false)

  const { searchMedsByName } = useMedsStore.getState()

  const onEmpty = () => {
    setIsLoading(false)
    setMedName('')
    lastTimeout && clearTimeout(lastTimeout)
    setLastTimeout(null)
    setMedications([])
  }

  const onTyping = (content: string) => {
    if (preventTyping) return
    if (content === '') {
      onEmpty()
      return
    }
    setIsLoading(true)
    setMedName(content)
    if (lastTimeout) {
      clearTimeout(lastTimeout)
    }
    const typingTimeOut = window.setTimeout(() => {
      searchMeds(content)
    }, 2000)
    setLastTimeout(typingTimeOut)
  }

  const searchMeds = async (name: string) => {
    setIsLoading(true)
    return searchMedsByName(name)
      .then(meds => {
        setMedications(meds)
        setPreventTyping(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const renderSearchedMed = ({ item }: { item: ISearchedMed }): JSX.Element => (
    <Pressable onPress={() => onSelect(item)}>
      <Box
        padding='16'
        marginY='04'
        marginRight='08'
        marginLeft='04'
        // @ts-ignore
        style={{
          borderRadius: '12px',
          borderWidth: '1px',
          borderColor: '#F9F9F9',
          boxShadow: '0px 2px 4px 0px rgba(62, 64, 136, 0.08)'
        }}
      >
        <Text font='display.16.bold'>{item.drug_name}</Text>
      </Box>
    </Pressable>
  )

  return (
    <Box
      overflowY='hidden'
      flexDirection='column'
      justifyContent='center'
      style={{ flexShrink: 1, height: '64vh' }}
    >
      <Text variant='h3' mb='24' textAlign='center'>
        Add Allergy
      </Text>
      <Formik
        initialValues={{ medication: '' }}
        onSubmit={() => {}}
        validateOnChange={false}
      >
        {() => (
          <Form>
            <TextFieldFormated
              name='medication'
              label='Drug Name'
              value={medName}
              onChangeText={text => onTyping(text)}
              placeholder='Search drug name'
              marginX='24'
            />
          </Form>
        )}
      </Formik>
      <FormHorizontalRule marginBottom='16' />
      {isLoading ? (
        <Box flex='1' justifyContent='center' alignItems='center'>
          <Loading />
        </Box>
      ) : (
        <Box style={{ flex: 1, overflow: 'hidden', paddingTop: '1em' }}>
          <ScrollView
            mb='20'
            style={{ flex: 1 }}
            contentContainerStyle={{ flexShrink: 1 }}
          >
            <FlatList
              pb='48'
              onEndReachedThreshold={0.5}
              onTouchStart={() => {
                setPreventTyping(true)
              }}
              data={medications}
              maxToRenderPerBatch={10}
              // @ts-ignore
              renderItem={({ item }: { item: ISearchedMed }) =>
                renderSearchedMed({ item })
              }
              keyExtractor={(item: ISearchedMed) => item.drug_name}
            />
          </ScrollView>
        </Box>
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * InsertAllergy (Step 2)
 * -------------------------------------------------------------------------- */

interface InsertAllergyProps {
  medication: ISearchedMed
  onCancel: () => void
  onSave: () => void
}

function InsertAllergy({ medication, onCancel, onSave }: InsertAllergyProps) {
  const { userId } = useContext(ChatContext)
  const { addUserAllergy } = useAllergyStore.getState()

  const formOptions = medication.dosage_forms.map(form => ({
    value: form.form,
    label: form.form
  }))
  const [selectedForm, setSelectedForm] = useState(formOptions?.[0])
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSave = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    setErrorMessage(null)

    const allergy: IAllergyInsert = {
      drugName: medication.drug_name,
      drugForm: selectedForm?.value || undefined,
      description: description.trim()
    }

    const result = await addUserAllergy(userId, allergy)

    if (result.success) {
      onSave()
      onCancel()
    } else {
      setErrorMessage(result.errorMessage || 'Something went wrong')
    }

    setIsSubmitting(false)
  }

  return (
    <Box
      overflowY='hidden'
      flexDirection='column'
      justifyContent='center'
      style={{ flexShrink: 1, maxHeight: '84vh' }}
    >
      <Box style={{ flex: 1, overflow: 'hidden' }}>
        <Text variant='h3' mb='24' textAlign='center'>
          {medication.drug_name}
        </Text>

        <ScrollView overflowY='auto'>
          <Formik
            initialValues={{}}
            onSubmit={() => {}}
            validateOnChange={false}
          >
            {() => (
              <Form>
                <Row>
                  <LeftColumn>Form</LeftColumn>
                  <RightColumn>
                    <SelectFieldFormated
                      name='allergyForm'
                      options={formOptions}
                      value={selectedForm}
                      defaultValue={formOptions[0]}
                      onChange={setSelectedForm}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }}
                    />
                  </RightColumn>
                </Row>
                <FormHorizontalRule />
                <Row>
                  <LeftColumn>Description</LeftColumn>
                  <RightColumn>
                    <TextFieldFormated
                      name='description'
                      label={null}
                      value={description}
                      valuePlaceholder='Write something...'
                      onChangeText={setDescription}
                    />
                  </RightColumn>
                </Row>
              </Form>
            )}
          </Formik>
        </ScrollView>

        {errorMessage && (
          <Text
            font='text.12.medium'
            textColor='red'
            mt='12'
            textAlign='center'
          >
            {errorMessage}
          </Text>
        )}

        <Box
          flexDirection='row'
          justifyContent='center'
          alignItems='center'
          gap='12'
          paddingTop='24'
          paddingBottom='12'
        >
          <Button variant='tertiary' width='fit-content' onPress={onCancel}>
            Cancel
          </Button>
          <Button
            variant='violetGradient'
            width='fit-content'
            onPress={handleSave}
          >
            {isSubmitting ? <Loading fill='white' size='small' /> : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * AddAllergyModal
 * -------------------------------------------------------------------------- */

enum AddAllergyState {
  Search = 'Search',
  Insert = 'Insert'
}

interface AddAllergyModalProps {
  open: boolean
  onRequestClose: () => void
  onSave: () => void
  hasClosed: boolean
}

function AddAllergyModal({
  open,
  onRequestClose,
  onSave,
  hasClosed
}: AddAllergyModalProps) {
  const [currentStep, setCurrentStep] = useState<AddAllergyState>(
    AddAllergyState.Search
  )
  const [selectedMedication, setSelectedMedication] =
    useState<ISearchedMed | null>(null)

  const handleSelectMedication = (medication: ISearchedMed) => {
    setSelectedMedication(medication)
    setCurrentStep(AddAllergyState.Insert)
  }

  useEffect(() => {
    if (hasClosed) {
      setCurrentStep(AddAllergyState.Search)
      setSelectedMedication(null)
    }
  }, [hasClosed])

  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      showCloseButton={true}
      width={411}
      maxHeight='90vh'
      borderRadius='medium'
      zIndex={0}
    >
      <Box>
        {currentStep === AddAllergyState.Search && (
          <SearchAllergy onSelect={handleSelectMedication} />
        )}
        {currentStep === AddAllergyState.Insert && (
          <InsertAllergy
            medication={selectedMedication}
            onCancel={() => {
              setCurrentStep(AddAllergyState.Search)
              onRequestClose()
            }}
            onSave={onSave}
          />
        )}
      </Box>
    </Modal>
  )
}

/* -----------------------------------------------------------------------------
 * RemoveAllergyModal
 * -------------------------------------------------------------------------- */

interface RemoveAllergyModalProps {
  open: boolean
  onRequestClose: () => void
  allergies: IAllergy[]
  onRemove: () => void
}

function RemoveAllergyModal({
  open,
  onRequestClose,
  allergies,
  onRemove
}: RemoveAllergyModalProps) {
  const { userId } = useContext(ChatContext)
  const { deleteUserAllergy } = useAllergyStore.getState()

  const [confirmingAllergy, setConfirmingAllergy] = useState<IAllergy | null>(
    null
  )
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!open) {
      setConfirmingAllergy(null)
      setIsDeleting(false)
    }
  }, [open])

  const handleConfirmDelete = async () => {
    if (!confirmingAllergy || isDeleting) return

    setIsDeleting(true)
    await deleteUserAllergy(userId, confirmingAllergy.id)
    setIsDeleting(false)
    setConfirmingAllergy(null)
    onRemove()
  }

  if (confirmingAllergy) {
    return (
      <Modal
        open={open}
        onRequestClose={onRequestClose}
        showCloseButton={true}
        width={411}
        maxHeight='90vh'
        borderRadius='medium'
        zIndex={0}
      >
        <Box flexDirection='column' padding='08'>
          <Text variant='h3' mb='20' textAlign='center'>
            Remove Allergy
          </Text>
          <Text font='text.14.medium' textAlign='center' mb='08'>
            Are you sure you want to remove the allergy{' '}
            <Text font='display.14.bold'>
              {confirmingAllergy.drugName}
              {confirmingAllergy.drugForm && ` (${confirmingAllergy.drugForm})`}
            </Text>
            ?
          </Text>

          <Box
            flexDirection='row'
            justifyContent='center'
            alignItems='center'
            gap='12'
            paddingTop='24'
            paddingBottom='12'
          >
            <Button
              variant='tertiary'
              width='fit-content'
              onPress={() => setConfirmingAllergy(null)}
            >
              Cancel
            </Button>
            <Button
              variant='violetGradient'
              width='fit-content'
              onPress={handleConfirmDelete}
            >
              {isDeleting ? <Loading fill='white' size='small' /> : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      showCloseButton={true}
      width={411}
      maxHeight='90vh'
      borderRadius='medium'
      zIndex={0}
    >
      <Box flexDirection='column' padding='08'>
        <Text variant='h3' mb='20' textAlign='center'>
          Remove Allergy
        </Text>

        {!allergies?.length ? (
          <Text
            font='text.14.medium'
            textColor='blueBayoux'
            textAlign='center'
            mb='20'
          >
            No allergies to remove.
          </Text>
        ) : (
          <ScrollView maxHeight='50vh'>
            {allergies.map(allergy => (
              <Pressable
                key={allergy.id}
                onPress={() => setConfirmingAllergy(allergy)}
              >
                <Box
                  padding='16'
                  marginY='04'
                  marginRight='08'
                  marginLeft='04'
                  // @ts-ignore
                  style={{
                    borderRadius: '12px',
                    borderWidth: '1px',
                    borderColor: '#F9F9F9',
                    boxShadow: '0px 2px 4px 0px rgba(62, 64, 136, 0.08)'
                  }}
                >
                  <Text font='display.14.bold'>
                    {allergy.drugName}
                    {allergy.drugForm && ` (${allergy.drugForm})`}
                  </Text>
                  {allergy.description && (
                    <Text font='text.12.medium' textColor='blueBayoux' mt='04'>
                      {allergy.description}
                    </Text>
                  )}
                </Box>
              </Pressable>
            ))}
          </ScrollView>
        )}

        <Box
          flexDirection='row'
          justifyContent='center'
          paddingTop='16'
          paddingBottom='12'
        >
          <Button
            variant='tertiary'
            width='fit-content'
            onPress={onRequestClose}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

/* -----------------------------------------------------------------------------
 * MemberAllergyPopover
 * -------------------------------------------------------------------------- */

interface MemberAllergyPopoverProps {
  allergies: IAllergy[]
  onAllergiesChanged: () => void
}

const MemberAllergyPopover = ({
  allergies,
  onAllergiesChanged
}: MemberAllergyPopoverProps) => {
  const { isAllergyPopoverOpen, openAllergyPopover, closePopovers } =
    React.useContext(ProfileContext)
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [hasAddClosed, setHasAddClosed] = useState(false)

  const closeAndOpen = (openFn: () => void) => {
    closePopovers()
    openFn()
  }

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false)
    setHasAddClosed(true)
  }

  useEffect(() => {
    if (isAddModalOpen) {
      setHasAddClosed(false)
    }
  }, [isAddModalOpen])

  return (
    <React.Fragment>
      <Button
        ref={setAnchor}
        variant='tertiary'
        size='fit'
        onPress={openAllergyPopover}
      >
        Edit Allergies
      </Button>
      <MemberInfoPopper open={isAllergyPopoverOpen} anchorEl={anchor}>
        <Box
          flexDirection='column'
          backgroundColor='white'
          borderRadius='large'
          boxShadow='large'
          maxHeight='200px'
          style={{ overflow: 'hidden' }}
        >
          <ScrollView padding='12'>
            <MenuItem
              label='Add Allergy'
              onPress={() => closeAndOpen(() => setIsAddModalOpen(true))}
            />
            <MenuItem
              label='Remove Allergy'
              onPress={() => closeAndOpen(() => setIsRemoveModalOpen(true))}
              lastItem
            />
          </ScrollView>
        </Box>
      </MemberInfoPopper>

      <AddAllergyModal
        open={isAddModalOpen}
        onRequestClose={handleCloseAddModal}
        onSave={onAllergiesChanged}
        hasClosed={hasAddClosed}
      />

      <RemoveAllergyModal
        open={isRemoveModalOpen}
        onRequestClose={() => setIsRemoveModalOpen(false)}
        allergies={allergies}
        onRemove={onAllergiesChanged}
      />
    </React.Fragment>
  )
}

export default MemberAllergyPopover
